import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantService, type Inquilino } from '@/services/tenantService';
import { debtService, type DeudaMensual } from '@/services/debtService';
import { paymentService } from '@/services/paymentService';
import { Button } from '@/components/ui/button';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

const TenantStatementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Inquilino | null>(null);
  const [debts, setDebts] = useState<DeudaMensual[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenantData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const fetchedTenant = await tenantService.getTenants().then(t => t.find(tn => tn.id_inquilino === id) as Inquilino | undefined);
      if (fetchedTenant) {
        setTenant(fetchedTenant);
        const fetchedDebts = await debtService.getDebtsByInquilino(id);
        // For each debt, fetch its payments
        const debtsWithPayments = await Promise.all(fetchedDebts.map(async (debt) => {
          const payments = await paymentService.getPaymentsByDebt(debt.id_deuda!);
          return { ...debt, payments };
        }));
        setDebts(debtsWithPayments);
        console.log("Debts with payments:", debtsWithPayments); // Add this line for debugging
      } else {
        setError("Inquilino no encontrado.");
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message);
      toast.error("Error al cargar los datos del inquilino.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantData();
  }, [id]);

  const breadcrumbs = [
    { label: "Inquilinos", path: "/tenants" },
    { label: tenant?.nombre_completo || "Cargando...", path: `/tenants/${id}` },
    { label: "Estado de Cuenta", path: `/tenants/${id}/statement` },
  ];

  if (loading) {
    return <div>Cargando estado de cuenta...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!tenant) {
    return <div>Inquilino no encontrado.</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <Breadcrumbs items={breadcrumbs} />
      <Button onClick={() => navigate(-1)} className="mb-4">Volver</Button>
      <h1 className="text-2xl font-bold mb-4">Estado de Cuenta de: {tenant.nombre_completo}</h1>

      {debts.length === 0 ? (
        <p>No hay deudas registradas para este inquilino.</p>
      ) : (
        <div className="space-y-6">
          {debts.map((debt) => (
            <div key={debt.id_deuda} className="border p-4 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold">Deuda del Mes: {debt.mes_correspondiente}</h3>
              <p>Monto Total Adeudado: {formatCurrency(debt.monto_total_adeudado)}</p>
              <p>Saldo Pendiente: {formatCurrency(debt.saldo_pendiente)}</p>
              <p>Pagada: {debt.esta_pagada ? 'Sí' : 'No'}</p>

              <h4 className="text-md font-semibold mt-4 mb-2">Pagos Recibidos</h4>
              {debt.payments && debt.payments.length > 0 ? (
                <ul className="list-disc pl-5">
                  {debt.payments.map((payment) => (
                    <li key={payment.id_registro_pago}>
                      {payment.fecha_pago.split('T')[0]} - {formatCurrency(payment.monto_pagado)} ({payment.metodo_pago})
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No hay pagos registrados para esta deuda.</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 p-4 border-t-2 border-gray-200">
        <h2 className="text-xl font-bold">Resumen de Deudas</h2>
        <p>Total de Deudas Pendientes: {formatCurrency(debts.reduce((sum, debt) => sum + debt.saldo_pendiente, 0))}</p>
      </div>
    </div>
  );
};

export default TenantStatementPage;
